import React, { useMemo } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from '@common/hooks';
import { DetailList } from '@scheduling/components/detail-list';
import { ShiftsCalendarDetailsCard } from '@scheduling/components';
import { RecipientsStatusLine } from './_recipients-status-line';
import { SpearfishingCampaignQueryCgResponse } from '@common/graphql/schema';
import { buildDialogUrl, capitalizeFirstLetter } from '@common/services';

export function Summary({
  spearfishingCampaign,
  hideShiftDetail,
}: {
  spearfishingCampaign:
    | SpearfishingCampaignQueryCgResponse['spearfishingCampaign']
    | undefined;
  hideShiftDetail?: boolean;
}) {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const campaign = spearfishingCampaign?.campaign;

  const formattedType = useMemo(() => {
    return campaign?.messageType === 'workforce_groups'
      ? 'Groups'
      : capitalizeFirstLetter(campaign?.messageType?.replace?.('_', ' ') || '');
  }, [campaign?.messageType]);

  const groups = useMemo(
    () =>
      spearfishingCampaign?.workforceGroups?.map((e) => e.name).join(', ') ||
      '',
    [spearfishingCampaign?.workforceGroups]
  );

  const shiftGap = campaign?.shiftGap;
  const shiftId = shiftGap?.id || '';
  const shiftStartTime = shiftGap
    ? moment.parseZone(shiftGap.startDatetime).tz(shiftGap.timeZone)
    : null;

  const shiftDetailsDrawerUrl = buildDialogUrl(
    'shifts-request-details-drawer',
    {
      props: {
        shiftId,
        type: type || '',
      },
    }
  );

  return (
    <>
      <DetailList
        items={[
          {
            label: 'Sent',
            value: campaign?.createdAt
              ? moment(campaign?.createdAt).format('dddd, MMM D HH:mm')
              : '',
          },
          {
            label: 'Sender',
            value: campaign?.createdBy?.worksUser?.name || '',
          },
          {
            label: 'Type',
            value: formattedType,
          },
          ...(campaign?.messageType === 'workforce_groups'
            ? [
                {
                  label: 'Groups',
                  value: groups,
                },
              ]
            : []),
          {
            label: 'Recipients',
            value: `${campaign?.spearfishingNotifications?.length || 0}`,
          },
          {
            label: 'Note',
            value: campaign?.message || 'N/A',
            valueStyle: {
              fontStyle: !campaign?.message ? 'italic' : undefined,
            },
          },
        ]}
      />
      <div style={{ height: 24 }} />
      <RecipientsStatusLine
        spearfishingNotifications={campaign?.spearfishingNotifications || []}
      />
      <div style={{ height: 24 }} />
      {shiftGap && !hideShiftDetail && (
        <ShiftsCalendarDetailsCard
          key={shiftGap.id}
          shiftGap={shiftGap}
          openingCount={shiftGap?.openShiftGapOpenings?.length || 0}
          onClick={() => navigate(shiftDetailsDrawerUrl)}
          style={{ margin: 0 }}
          titlePrefix={`${shiftStartTime?.format('dddd, MMM D')} `}
        />
      )}
    </>
  );
}
